/**
 * The filter panel: four tabs over the catalogue's own facets, and `Język` — the content language,
 * which is its own tab by the owner's call and not a facet (`ExerciseLanguageChoice`).
 *
 * Base UI supplies the popup — the portal, the scrim, the focus trap, the dismissal and the
 * positioning — and every visual byte is ours. The SHELL is `shared/ui/filterPanel.module.css`,
 * transcribed from the Programy panel this screen's specimen copies verbatim; the four bodies are
 * this screen's own.
 *
 * ── The facets are the ENTITY's, and this file does not re-derive one ───────────────────────
 * `exerciseFacetGroups` already answers which options exist, how many movements carry each, which
 * are ticked and what a chip press means. Four tabs draw four of its groups; a press dispatches
 * `toggleExerciseFacet`. Nothing here counts anything.
 *
 * ── `Źródło` writes the SCOPE, not a facet ──────────────────────────────────────────────────
 * The canon draws the source both ways: as the radio row above the table and as a tab in here. They
 * are ONE value — the catalogue's `origin` — and this tab dispatches `onScopeChange` so the switch
 * and the panel cannot drift apart. That is also why the toolbar's dot does not count it: the
 * switch is already saying which list this is, in words, four inches above the square.
 *
 * ── The facets apply as they are pressed, and `Pokaż N` only closes ─────────────────────────
 * No draft and no apply step — the plan library's own wiring, and the canon's. The figure is what
 * the table is showing behind the scrim.
 */

import { Popover } from '@base-ui/react/popover';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MUSCLE_REGIONS,
  EXERCISE_ORIGINS,
  EXERCISE_TYPE_KEYS,
  ExerciseLanguageChoice,
  emptyExerciseQuery,
  exerciseFacetGroups,
  muscleGroupLabelKey,
  toggleExerciseFacet,
  toggleExerciseTag,
  type ExerciseCatalogueView,
  type ExerciseFacetChipView,
  type ExerciseFacetKey,
  type ExerciseLibraryFilterTab,
  type ExerciseQuery,
  type ExerciseTagOption,
} from '~/entities/exercise-library';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';
import { exerciseTypeColour } from '~/entities/exercise-library';
import photoBox from '~/shared/ui/photoBox.module.css';

import { originOfScope, scopeOfOrigin, type ExerciseScope } from '../model/exerciseScope';

import { CheckGlyph } from '~/shared/ui/FormGlyphs';
import shell from '~/shared/ui/filterPanel.module.css';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import styles from './ExerciseFilterPanel.module.css';

/**
 * The four facet tabs, in the specimen's own order, and the owner's `Język` after them.
 *
 * The keys are the entity's, so a group this build draws and a group the entity answers for cannot
 * fall out of step. `language` is not a facet: it narrows nothing and is never counted.
 */
const TABS: readonly ExerciseLibraryFilterTab[] = [
  'types',
  'muscles',
  'equipment',
  'origin',
  'language',
];

/**
 * The panel's tabs on this screen: the entity's five, and „Tagi" between Źródło and Język (canon
 * `F3-filtr`, 10 October 2026). Tags are shown on the library page and nowhere else (R23), so the
 * tab is this screen's and not the entity's — the builder's drawer shares the entity's panel and
 * must not grow it.
 */
type PanelTab = ExerciseLibraryFilterTab | 'tags';

/** The tab list for a catalogue: „Tagi" only for a trainer who has a tag to filter by. */
function panelTabs(hasTags: boolean): readonly PanelTab[] {
  return hasTags ? [...TABS.slice(0, 4), 'tags', ...TABS.slice(4)] : TABS;
}

/** The offset that hangs the panel over the list card's own top-left corner. */
const PANEL_INSET_TOP = 19.5;
const PANEL_INSET_LEFT = -6;

interface ExerciseFilterPanelProps {
  readonly query: ExerciseQuery;
  readonly onQueryChange: (query: ExerciseQuery) => void;
  readonly catalogue: ExerciseCatalogueView;
  readonly scope: ExerciseScope;
  readonly onScopeChange: (scope: ExerciseScope) => void;
  readonly matchCount: number;
  readonly anchor: React.RefObject<HTMLElement | null>;
  /** Open the manage dialog. The panel closes first: the dialog is the next thing on screen. */
  readonly onManageTags: () => void;
  readonly children: React.ReactNode;
}

export function ExerciseFilterPanel({
  query,
  onQueryChange,
  catalogue,
  scope,
  onScopeChange,
  matchCount,
  anchor,
  onManageTags,
  children,
}: ExerciseFilterPanelProps) {
  const { t } = useTranslation();
  const tabs = panelTabs(catalogue.tags.length > 0);
  const [chosenTab, setTab] = useState<PanelTab>('types');
  // The last tag deleted under an open „Tagi" tab takes the tab with it; the panel then stands on
  // its first tab rather than on a body that no longer exists.
  const tab = tabs.includes(chosenTab) ? chosenTab : 'types';
  /* ONE body and ONE id for it.
   *
   * Only the active tab's body is mounted, so four per-tab ids meant three of the four tabs
   * pointed `aria-controls` at elements that did not exist — an `aria-controls` naming a missing id
   * is dropped, so three of the four tabs claimed to control nothing. One stable id is the honest
   * shape for one swapped panel, and `aria-labelledby` on the body still names whichever tab is
   * open. `useId` because two filter panels can be mounted at once on a screen that grows one. */
  const panelId = useId();
  const tabId = (entry: PanelTab) => `${panelId}-tab-${entry}`;

  const groups = exerciseFacetGroups(catalogue, {
    group: (facet) => t(`web:exerciseLibrary.filters.tabs.${facet}`),
    muscle: (muscle) => t(muscleGroupLabelKey(muscle)),
    // The `web:` group, which carries all seven — the upstream one has six and no `custom`, and
    // mixing namespaces inside one legend is what that avoids.
    type: (type) => t(`web:exerciseLibrary.type.${type}`),
    origin: (origin) => t(`web:exerciseLibrary.filters.origin.${origin}`),
    everything: t('web:exerciseLibrary.filters.origin.all'),
  });
  const chipsOf = (facet: ExerciseFacetKey): readonly ExerciseFacetChipView[] =>
    groups.find((group) => group.facet === facet)?.chips ?? [];

  const toggle = (facet: ExerciseFacetKey, id: string) => {
    onQueryChange(toggleExerciseFacet(query, facet, id));
  };

  const counts: Readonly<Record<PanelTab, number>> = {
    tags: catalogue.tags.filter((option) => option.isSelected).length,
    types: query.types.length,
    muscles: query.muscles.length,
    equipment: query.equipment.length,
    // The scope is never counted as a facet — see the header.
    origin: 0,
    // Nor is the content language: it narrows nothing.
    language: 0,
  };

  return (
    <Popover.Root modal="trap-focus">
      {children}
      <Popover.Portal>
        <Popover.Backdrop className={shell.scrim} />
        <Popover.Positioner
          className={shell.positioner}
          anchor={anchor}
          side="bottom"
          align="start"
          // The panel's top edge sits INSIDE the card's, so the offset is the card's own height less
          // the inset — the only way to express "hang over its top-left corner" against an anchor
          // the positioner measures from the outside.
          sideOffset={({ anchor: box }) => PANEL_INSET_TOP - box.height}
          alignOffset={PANEL_INSET_LEFT}
          collisionAvoidance={{ side: 'none', align: 'none' }}
        >
          <Popover.Popup className={shell.panel}>
            <div className={shell.head}>
              <Popover.Title className={shell.title}>
                {t('web:exerciseLibrary.filters.title')}
              </Popover.Title>
              <span className={shell.spacer} aria-hidden />
              <Popover.Close
                className={shell.close}
                aria-label={t('web:exerciseLibrary.filters.close')}
              >
                <span aria-hidden>✕</span>
              </Popover.Close>
            </div>

            {/* A real tab list: one tab stop, arrows between the tabs, and each body labelled by
             * the tab that opened it. */}
            <div
              className={shell.tabs}
              role="tablist"
              aria-label={t('web:exerciseLibrary.filters.title')}
            >
              {tabs.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  role="tab"
                  id={tabId(entry)}
                  aria-controls={panelId}
                  aria-selected={tab === entry}
                  tabIndex={tab === entry ? 0 : -1}
                  className={`${String(shell.tab)} ${tab === entry ? String(shell.tabOn) : ''}`}
                  onClick={() => {
                    setTab(entry);
                  }}
                  onKeyDown={(event) => {
                    const delta =
                      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                    if (delta === 0) return;
                    event.preventDefault();
                    const at = tabs.indexOf(tab);
                    // Wraps at both ends, which is the tab pattern's own rule and the opposite of
                    // the LIST's — a tab strip is a ring of four, a list is a column of rows.
                    const next = tabs[(at + delta + tabs.length) % tabs.length];
                    if (next === undefined) return;
                    setTab(next);
                    document.getElementById(tabId(next))?.focus();
                  }}
                >
                  {t(`web:exerciseLibrary.filters.tabs.${entry}`)}
                  {counts[entry] > 0 && <b className={shell.tabCount}>{counts[entry]}</b>}
                </button>
              ))}
            </div>

            <div
              className={shell.body}
              role="tabpanel"
              id={panelId}
              aria-labelledby={tabId(tab)}
            >
              {tab === 'types' ? (
                <TypeBody chips={chipsOf('types')} onToggle={toggle} />
              ) : tab === 'muscles' ? (
                <MuscleBody chips={chipsOf('muscles')} onToggle={toggle} />
              ) : tab === 'equipment' ? (
                <EquipmentBody chips={chipsOf('equipment')} onToggle={toggle} />
              ) : tab === 'tags' ? (
                <TagBody
                  tags={catalogue.tags}
                  onToggle={(key) => {
                    onQueryChange(toggleExerciseTag(query, key));
                  }}
                />
              ) : tab === 'language' ? (
                <ExerciseLanguageChoice titleClassName={String(shell.sectionTitle)} />
              ) : (
                <SourceBody chips={chipsOf('origin')} scope={scope} onScopeChange={onScopeChange} />
              )}
            </div>

            <div className={shell.foot}>
              <button
                type="button"
                className={shell.clear}
                onClick={() => {
                  // The SCOPE is untouched: it is the address, it is visible in words above the
                  // table, and clearing a filter must not move the trainer to a different list.
                  onQueryChange({
                    ...emptyExerciseQuery(),
                    origin: query.origin,
                  });
                }}
              >
                {t('web:exerciseLibrary.filters.clear')}
              </button>
              {tab === 'tags' && (
                // Closes the panel AND opens the dialog: Base UI's own close, so focus and the
                // scrim are handed back before the dialog takes them.
                <Popover.Close
                  className={`${String(shell.clear)} ${String(styles.manageTags)}`}
                  onClick={onManageTags}
                >
                  {t('web:exerciseLibrary.tags.manageAll')}
                </Popover.Close>
              )}
              <span className={shell.spacer} aria-hidden />
              {/* Closes, and nothing else — the facets are already applied. The figure is what the
               * table is showing behind the scrim. */}
              <Popover.Close className={shell.apply}>
                {t('web:exerciseLibrary.filters.apply')}
                <b className={shell.applyCount}>{matchCount}</b>
              </Popover.Close>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

interface BodyProps {
  readonly chips: readonly ExerciseFacetChipView[];
  readonly onToggle: (facet: ExerciseFacetKey, id: string) => void;
}

/** Typ — a chip per type the catalogue actually holds, carrying its glyph and its hue. */
function TypeBody({ chips, onToggle }: BodyProps) {
  const { t } = useTranslation();
  return (
    <>
      <span className={shell.sectionTitle}>{t('web:exerciseLibrary.filters.tabs.types')}</span>
      <div className={styles.chips}>
        {chips.map((chip) => {
          // RESOLVED against the taxonomy rather than cast: a chip id is a string, and a cast would
          // hand the colour table a key it has no entry for. The entity's own `toggleExerciseFacet`
          // resolves the same way, for the same reason.
          const type = EXERCISE_TYPE_KEYS.find((candidate) => candidate === chip.id) ?? null;
          return (
            <label
              key={chip.id}
              className={`${String(styles.chip)} ${chip.isSelected ? String(styles.chipOn) : ''}`}
              style={exerciseTypeColour(type)}
            >
              <input
                type="checkbox"
                className={styles.control}
                checked={chip.isSelected}
                onChange={() => {
                  onToggle('types', chip.id);
                }}
              />
              <ExerciseTypeGlyph type={type} className={styles.chipGlyph} />
              {chip.label}
              {/* The tick is the second channel beside the fill and the ink; the checkbox's own
               * state is the third, and it is the one assistive technology reads. */}
              {chip.isSelected ? (
                <b className={styles.chipCheck} aria-hidden>
                  ✓
                </b>
              ) : null}
            </label>
          );
        })}
      </div>
    </>
  );
}

/** Mięśnie — the fourteen groups, broken into the specimen's four regions. */
function MuscleBody({ chips, onToggle }: BodyProps) {
  const { t } = useTranslation();
  const byId = new Map(chips.map((chip) => [chip.id, chip]));

  return (
    <>
      <span className={shell.sectionTitle}>{t('web:exerciseLibrary.filters.tabs.muscles')}</span>
      <div className={styles.regions}>
        {MUSCLE_REGIONS.map((region) => {
          // Only the groups the catalogue actually holds: the entity drops a facet value no
          // movement carries, and a region left with none of its own is not drawn at all.
          const offered = region.groups.filter((group) => byId.has(group));
          if (offered.length === 0) return null;
          return (
            <div key={region.key}>
              <span className={styles.regionTitle}>
                {t(`web:exerciseLibrary.filters.regions.${region.key}`)}
              </span>
              {offered.map((group) => {
                const chip = byId.get(group);
                if (chip === undefined) return null;
                return (
                  <label
                    key={group}
                    className={`${String(styles.check)} ${
                      chip.isSelected ? String(styles.checkOn) : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className={styles.control}
                      checked={chip.isSelected}
                      onChange={() => {
                        onToggle('muscles', chip.id);
                      }}
                    />
                    <span className={styles.box} aria-hidden>
                      <CheckGlyph className={styles.boxGlyph} />
                    </span>
                    {chip.label}
                    <b className={styles.checkCount}>{chip.count}</b>
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Sprzęt — a grid of the pieces' own photographs, six across, in their own scroller. */
function EquipmentBody({ chips, onToggle }: BodyProps) {
  const { t } = useTranslation();
  return (
    <>
      <span className={shell.sectionTitle}>{t('web:exerciseLibrary.filters.tabs.equipment')}</span>
      <div className={styles.equipment}>
        {chips.map((chip) => (
          <label
            key={chip.id}
            className={`${String(styles.equipCell)} ${
              chip.isSelected ? String(styles.equipCellOn) : ''
            }`}
          >
            <input
              type="checkbox"
              className={styles.control}
              checked={chip.isSelected}
              onChange={() => {
                onToggle('equipment', chip.id);
              }}
            />
            {chip.imageUrl === null ? (
              <i className={styles.equipLetter} aria-hidden>
                {chip.label.slice(0, 1).toUpperCase()}
              </i>
            ) : (
              <img
                className={`${String(photoBox.photo)} ${String(styles.equipImage)}`}
                src={chip.imageUrl}
                alt=""
                loading="lazy"
                decoding="async"
              />
            )}
            <span className={styles.equipLabel}>{chip.label}</span>
          </label>
        ))}
      </div>
    </>
  );
}

/**
 * Źródło — three radio rows, and they write the SCOPE.
 *
 * The same value the switch above the table writes, so the two cannot disagree. `EXERCISE_ORIGINS`
 * is the entity's order and `model/exerciseScope` owns BOTH directions of the mapping — the inverse
 * was a ternary in this file, where nothing could test it.
 *
 * A `<fieldset>` with an sr-only `<legend>`, exactly as `ExerciseScopeSwitch` does it: three
 * `name`d radios with no group around them are three unrelated controls to a screen reader, which
 * announces neither how many there are nor what they are choosing between.
 */
function SourceBody({
  chips,
  scope,
  onScopeChange,
}: {
  readonly chips: readonly ExerciseFacetChipView[];
  readonly scope: ExerciseScope;
  readonly onScopeChange: (scope: ExerciseScope) => void;
}) {
  const { t } = useTranslation();
  const byId = new Map(chips.map((chip) => [chip.id, chip]));
  const current = originOfScope(scope);

  return (
    <>
      <fieldset className={styles.sourceGroup}>
        <legend className={`${String(shell.sectionTitle)} ${String(styles.sourceLegend)}`}>
          {t('web:exerciseLibrary.filters.tabs.origin')}
        </legend>
        <div className={styles.sources}>
        {EXERCISE_ORIGINS.map((origin) => {
          const chip = byId.get(origin);
          const selected = origin === current;
          return (
            <label
              key={origin}
              className={`${String(styles.source)} ${selected ? String(styles.sourceOn) : ''}`}
            >
              <input
                type="radio"
                name="exercise-filter-origin"
                className={styles.control}
                checked={selected}
                onChange={() => {
                  onScopeChange(scopeOfOrigin(origin));
                }}
              />
              <span className={styles.dot} aria-hidden />
              {t(`web:exerciseLibrary.filters.origin.${origin}`)}
              {chip !== undefined && <b className={styles.sourceCount}>{chip.count}</b>}
            </label>
          );
        })}
        </div>
      </fieldset>
    </>
  );
}

/**
 * Tagi — one checkbox row per tag the trainer has, with how many movements carry it, and the one
 * sentence saying the rows are OR'd. The same selection the bar over the table toggles.
 */
function TagBody({
  tags,
  onToggle,
}: {
  readonly tags: readonly ExerciseTagOption[];
  readonly onToggle: (key: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className={shell.sectionTitle}>{t('web:exerciseLibrary.tags.title')}</span>
      <div className={`${String(scrollbar.scrollbar)} ${String(styles.tagRows)}`}>
        {tags.map((tag) => (
          <label
            key={tag.value}
            className={`${String(styles.check)} ${tag.isSelected ? String(styles.checkOn) : ''}`}
          >
            <input
              type="checkbox"
              className={styles.control}
              checked={tag.isSelected}
              onChange={() => {
                onToggle(tag.value);
              }}
            />
            <span className={styles.box} aria-hidden>
              <CheckGlyph className={styles.boxGlyph} />
            </span>
            <span className={styles.tagLabel}>{tag.label}</span>
            <b className={styles.checkCount}>{tag.count}</b>
          </label>
        ))}
      </div>
      <p className={styles.tagNote}>{t('web:exerciseLibrary.tags.filterNote')}</p>
    </>
  );
}
